import React, { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { MODULE_LABELS, RbacModule } from "@/rbac/modules";

interface ScreenGuardProps {
  module: RbacModule;
  children: ReactNode;
}

/**
 * Blocks an entire route if the user has no view permission for the module.
 * Renders a friendly "no access" card instead.
 */
export const ScreenGuard: React.FC<ScreenGuardProps> = ({
  module,
  children,
}) => {
  const { can, isLoading, rbac } = useAuth();
  if (isLoading) return null;
  if (!rbac) return null;
  if (can(module, "view")) return <>{children}</>;

  return (
    <div className="flex min-h-[70vh] items-center justify-center p-8">
      <div className="max-w-md w-full rounded-card border border-border bg-card p-8 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <Lock className="h-6 w-6 text-muted-foreground" />
        </div>
        <h2 className="text-lg font-semibold text-foreground">
          Access restricted
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          You don&apos;t have permission to view {MODULE_LABELS[module]}. Ask an
          administrator if you need access.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center justify-center rounded-btn bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
};
